import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const PAGE = 10;

const Entry = ({ x }) => (
  <li className="px-4 py-3 text-sm" data-testid={`pax-log-${x.id}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <span className="font-medium text-slate-900">{x.rederi} · {x.period_label}</span>
      <span className="text-xs text-slate-500">{fmtDate(x.ts)}</span>
    </div>
    <div className="text-slate-600 mt-0.5">
      {x.manual ? `Rapport registrert manuelt (mottatt ${x.received_date.split("-").reverse().join(".")}) · ${x.total_after} passasjerer` : (
        <>
          {x.changes.map((c) => `${c.date.split("-").reverse().join(".")}: ${c.before} → ${c.after}`).join(", ")}
          <span className="text-slate-400"> · sum {x.total_before} → {x.total_after}</span>
        </>
      )}
    </div>
    <div className="text-xs text-slate-500 mt-0.5">{x.manual ? "Registrert" : "Endret"} av {x.user_name ? `${x.user_name} (${x.user_email})` : x.user_email}{x.role === "super_admin" ? " · Super Admin" : ""}</div>
  </li>
);

export const PaxLog = ({ cid, refreshKey }) => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const load = useCallback(() => {
    api.get(`/customers/${cid}/pax/log`, { params: { skip: page * PAGE, limit: PAGE } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [cid, page]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const pages = Math.max(1, Math.ceil(data.total / PAGE));
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="pax-log">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-slate-50 rounded-lg" data-testid="pax-log-toggle">
        <span className="flex items-baseline gap-2">
          <span className="font-display text-lg font-semibold text-slate-900">Endringslogg</span>
          <span className="text-sm text-slate-500" data-testid="pax-log-total">{data.total} endringer</span>
        </span>
        <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          {data.items.length === 0 ? <p className="px-4 pb-4 text-sm text-slate-500">Ingen endringer eller manuelle registreringer ennå.</p> : (
            <ul className="divide-y divide-slate-100 border-t border-slate-100">{data.items.map((x) => <Entry key={x.id} x={x} />)}</ul>
          )}
          {data.total > PAGE && (
            <div className="flex items-center justify-between gap-3 p-3 border-t border-slate-100">
              <span className="text-sm text-slate-500">Side {page + 1} av {pages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="pax-log-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} data-testid="pax-log-next"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
